import client from "./client";
import type {
    TimeOffCategory,
    TimeOffPolicySummary,
    TimeOffPolicyDetail,
    TimeOffCalendarHoliday,
    TimeOffCalendarResponse,
    CreateTimeOffCategoryBody,
    PatchTimeOffCategoryBody,
    CreateTimeOffPolicyBody,
    PatchTimeOffPolicyBody,
    AddPolicyCategoryBody,
    PatchPolicyCategoryBody,
    AddPolicyTierBody,
    PatchPolicyTierBody,
    TimeOffPolicyCategoryDetail,
    TimeOffPolicyTier,
    HolidayCalendarEntry,
    HolidayPolicyDetail,
    CreateHolidayCalendarBody,
    PatchHolidayCalendarBody,
    CreateHolidayPolicyBody,
    PatchHolidayPolicyBody,
    EmployeeTimeOffPolicyResponse,
    AssignEmployeeTimeOffPolicyBody,
    EmployeeTimeOffBalanceRow,
    EmployeeTimeOffLedgerResponse,
} from "../interfaces/TimeOff";

export function getTimeOffErrorMessage(err: unknown): string {
    if (err && typeof err === "object" && "response" in err) {
        const r = (err as { response?: { data?: { message?: string } } }).response;
        if (r?.data?.message) return r.data.message;
    }
    if (err instanceof Error) return err.message;
    return "Request failed";
}

const MS_PER_DAY = 86400000;

function formatYmd(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
}

function parseYmdLocal(s: string): Date {
    const parts = s.split("-").map(Number);
    if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) {
        return new Date(NaN);
    }
    const [y, m, d] = parts;
    return new Date(y, m - 1, d);
}

/** Split [startDate, endDate] into windows of at most `maxInclusiveDays` inclusive days (API limit 90). */
export function chunkDateRangeInclusive(
    startDate: string,
    endDate: string,
    maxInclusiveDays = 90
): { start: string; end: string }[] {
    const start = parseYmdLocal(startDate);
    const end = parseYmdLocal(endDate);
    if (isNaN(start.getTime()) || isNaN(end.getTime()) || start > end) {
        return [];
    }
    const chunks: { start: string; end: string }[] = [];
    let cur = new Date(start);
    const endMs = end.getTime();
    while (cur.getTime() <= endMs) {
        const chunkStart = new Date(cur);
        const chunkEndCandidate = new Date(chunkStart);
        chunkEndCandidate.setDate(chunkEndCandidate.getDate() + (maxInclusiveDays - 1));
        const chunkEnd =
            chunkEndCandidate.getTime() > endMs ? new Date(end) : chunkEndCandidate;
        chunks.push({
            start: formatYmd(chunkStart),
            end: formatYmd(chunkEnd),
        });
        cur = new Date(chunkEnd);
        cur.setDate(cur.getDate() + 1);
    }
    return chunks;
}

// --- Categories ---

export async function getTimeOffCategories(activeOnly?: boolean): Promise<TimeOffCategory[]> {
    const response = await client.get<TimeOffCategory[]>("admin/timeOff/categories", {
        params: activeOnly === undefined ? {} : { activeOnly },
    });
    return response.data;
}

export async function createTimeOffCategory(body: CreateTimeOffCategoryBody): Promise<TimeOffCategory> {
    const response = await client.post<TimeOffCategory>("admin/timeOff/categories", body);
    return response.data;
}

export async function patchTimeOffCategory(
    id: number,
    body: PatchTimeOffCategoryBody
): Promise<TimeOffCategory> {
    const response = await client.patch<TimeOffCategory>(`admin/timeOff/categories/${id}`, body);
    return response.data;
}

export async function activateTimeOffCategory(id: number): Promise<{ timeOffCategoryID: number; active: boolean }> {
    const response = await client.post<{ timeOffCategoryID: number; active: boolean }>(
        `admin/timeOff/categories/${id}/activate`,
        {}
    );
    return response.data;
}

export async function deactivateTimeOffCategory(id: number): Promise<{ timeOffCategoryID: number; active: boolean }> {
    const response = await client.post<{ timeOffCategoryID: number; active: boolean }>(
        `admin/timeOff/categories/${id}/deactivate`,
        {}
    );
    return response.data;
}

// --- Policies ---

export async function getTimeOffPolicies(activeOnly?: boolean): Promise<TimeOffPolicySummary[]> {
    const response = await client.get<TimeOffPolicySummary[]>("admin/timeOff/policies", {
        params: activeOnly === undefined ? {} : { activeOnly },
    });
    return response.data;
}

export async function getTimeOffPolicyById(id: number): Promise<TimeOffPolicyDetail> {
    const response = await client.get<TimeOffPolicyDetail>(`admin/timeOff/policies/${id}`);
    return response.data;
}

export async function createTimeOffPolicy(body: CreateTimeOffPolicyBody): Promise<TimeOffPolicyDetail> {
    const response = await client.post<TimeOffPolicyDetail>("admin/timeOff/policies", body);
    return response.data;
}

export async function patchTimeOffPolicy(id: number, body: PatchTimeOffPolicyBody): Promise<TimeOffPolicySummary> {
    const response = await client.patch<TimeOffPolicySummary>(`admin/timeOff/policies/${id}`, body);
    return response.data;
}

export async function activateTimeOffPolicy(id: number): Promise<{ timeOffPolicyID: number; active: boolean }> {
    const response = await client.post<{ timeOffPolicyID: number; active: boolean }>(
        `admin/timeOff/policies/${id}/activate`,
        {}
    );
    return response.data;
}

export async function deactivateTimeOffPolicy(id: number): Promise<{ timeOffPolicyID: number; active: boolean }> {
    const response = await client.post<{ timeOffPolicyID: number; active: boolean }>(
        `admin/timeOff/policies/${id}/deactivate`,
        {}
    );
    return response.data;
}

export async function addPolicyCategory(
    policyId: number,
    body: AddPolicyCategoryBody
): Promise<TimeOffPolicyCategoryDetail> {
    const response = await client.post<TimeOffPolicyCategoryDetail>(
        `admin/timeOff/policies/${policyId}/categories`,
        body
    );
    return response.data;
}

export async function patchPolicyCategory(
    policyId: number,
    policyCategoryId: number,
    body: PatchPolicyCategoryBody
): Promise<TimeOffPolicyCategoryDetail> {
    const response = await client.patch<TimeOffPolicyCategoryDetail>(
        `admin/timeOff/policies/${policyId}/categories/${policyCategoryId}`,
        body
    );
    return response.data;
}

export async function deactivatePolicyCategory(
    policyId: number,
    policyCategoryId: number
): Promise<{ timeOffPolicyCategoryID: number; active: boolean }> {
    const response = await client.post<{ timeOffPolicyCategoryID: number; active: boolean }>(
        `admin/timeOff/policies/${policyId}/categories/${policyCategoryId}/deactivate`,
        {}
    );
    return response.data;
}

export async function addPolicyTier(
    policyId: number,
    policyCategoryId: number,
    body: AddPolicyTierBody
): Promise<TimeOffPolicyTier> {
    const response = await client.post<TimeOffPolicyTier>(
        `admin/timeOff/policies/${policyId}/categories/${policyCategoryId}/tiers`,
        body
    );
    return response.data;
}

export async function patchPolicyTier(
    policyId: number,
    policyCategoryId: number,
    tierId: number,
    body: PatchPolicyTierBody
): Promise<TimeOffPolicyTier> {
    const response = await client.patch<TimeOffPolicyTier>(
        `admin/timeOff/policies/${policyId}/categories/${policyCategoryId}/tiers/${tierId}`,
        body
    );
    return response.data;
}

export async function deletePolicyTier(
    policyId: number,
    policyCategoryId: number,
    tierId: number
): Promise<void> {
    await client.delete(`admin/timeOff/policies/${policyId}/categories/${policyCategoryId}/tiers/${tierId}`);
}

// --- Holiday Calendar ---

export async function getHolidayCalendar(activeOnly?: boolean): Promise<HolidayCalendarEntry[]> {
    const response = await client.get<HolidayCalendarEntry[]>("admin/timeOff/holidayCalendar", {
        params: activeOnly === undefined ? {} : { activeOnly },
    });
    return response.data;
}

export async function createHolidayCalendar(body: CreateHolidayCalendarBody): Promise<HolidayCalendarEntry> {
    const response = await client.post<HolidayCalendarEntry>("admin/timeOff/holidayCalendar", body);
    return response.data;
}

export async function patchHolidayCalendar(id: number, body: PatchHolidayCalendarBody): Promise<HolidayCalendarEntry> {
    const response = await client.patch<HolidayCalendarEntry>(`admin/timeOff/holidayCalendar/${id}`, body);
    return response.data;
}

export async function activateHolidayCalendar(id: number): Promise<{ holidayCalendarID: number; active: boolean }> {
    const response = await client.post<{ holidayCalendarID: number; active: boolean }>(
        `admin/timeOff/holidayCalendar/${id}/activate`,
        {}
    );
    return response.data;
}

export async function deactivateHolidayCalendar(id: number): Promise<{ holidayCalendarID: number; active: boolean }> {
    const response = await client.post<{ holidayCalendarID: number; active: boolean }>(
        `admin/timeOff/holidayCalendar/${id}/deactivate`,
        {}
    );
    return response.data;
}

// --- Holiday Policies ---

export async function getHolidayPolicies(activeOnly?: boolean): Promise<HolidayPolicyDetail[]> {
    const response = await client.get<HolidayPolicyDetail[]>("admin/timeOff/holidayPolicies", {
        params: activeOnly === undefined ? {} : { activeOnly },
    });
    return response.data;
}

export async function getHolidayPolicyById(id: number): Promise<HolidayPolicyDetail> {
    const response = await client.get<HolidayPolicyDetail>(`admin/timeOff/holidayPolicies/${id}`);
    return response.data;
}

export async function createHolidayPolicy(body: CreateHolidayPolicyBody): Promise<HolidayPolicyDetail> {
    const response = await client.post<HolidayPolicyDetail>("admin/timeOff/holidayPolicies", body);
    return response.data;
}

export async function patchHolidayPolicy(id: number, body: PatchHolidayPolicyBody): Promise<HolidayPolicyDetail> {
    const response = await client.patch<HolidayPolicyDetail>(`admin/timeOff/holidayPolicies/${id}`, body);
    return response.data;
}

export async function activateHolidayPolicy(id: number): Promise<{ holidayPolicyID: number; active: boolean }> {
    const response = await client.post<{ holidayPolicyID: number; active: boolean }>(
        `admin/timeOff/holidayPolicies/${id}/activate`,
        {}
    );
    return response.data;
}

export async function deactivateHolidayPolicy(id: number): Promise<{ holidayPolicyID: number; active: boolean }> {
    const response = await client.post<{ holidayPolicyID: number; active: boolean }>(
        `admin/timeOff/holidayPolicies/${id}/deactivate`,
        {}
    );
    return response.data;
}

export async function addHolidayToPolicy(policyId: number, holidayCalendarID: number): Promise<HolidayPolicyDetail> {
    const response = await client.post<HolidayPolicyDetail>(
        `admin/timeOff/holidayPolicies/${policyId}/holidays`,
        { holidayCalendarID }
    );
    return response.data;
}

export async function removeHolidayFromPolicy(policyId: number, holidayCalendarID: number): Promise<{ deleted: boolean }> {
    const response = await client.delete<{ deleted: boolean }>(
        `admin/timeOff/holidayPolicies/${policyId}/holidays/${holidayCalendarID}`
    );
    return response.data;
}

// --- Employee Time Off Policy Assignment ---

export async function getEmployeeTimeOffPolicy(employeeId: number): Promise<EmployeeTimeOffPolicyResponse> {
    const response = await client.get<EmployeeTimeOffPolicyResponse>(
        `admin/employees/${employeeId}/timeOff/policy`
    );
    return response.data;
}

export async function assignEmployeeTimeOffPolicy(
    employeeId: number,
    body: AssignEmployeeTimeOffPolicyBody
): Promise<void> {
    await client.post(`admin/employees/${employeeId}/timeOff/policy`, body);
}

// --- Employee Time Off Balances & Ledger (read-only) ---

export async function getEmployeeTimeOffBalance(employeeId: number): Promise<EmployeeTimeOffBalanceRow[]> {
    const response = await client.get<EmployeeTimeOffBalanceRow[]>(
        `admin/employees/${employeeId}/timeOff/balance`
    );
    return response.data;
}

export async function getEmployeeTimeOffLedger(
    employeeId: number,
    categoryId: number,
    page = 0,
    pageSize = 50
): Promise<EmployeeTimeOffLedgerResponse> {
    const response = await client.get<EmployeeTimeOffLedgerResponse>(
        `admin/employees/${employeeId}/timeOff/ledger/${categoryId}`,
        { params: { page, pageSize } }
    );
    return response.data;
}

// --- Calendar (holidays preview; chunks ≤90 days) ---

export async function getTimeOffCalendarHolidaysMerged(
    startDate: string,
    endDate: string
): Promise<TimeOffCalendarHoliday[]> {
    const chunks = chunkDateRangeInclusive(startDate, endDate, 90);
    const merged = new Map<string, TimeOffCalendarHoliday>();
    for (const ch of chunks) {
        const response = await client.get<TimeOffCalendarResponse>("admin/timeOff/calendar", {
            params: { startDate: ch.start, endDate: ch.end },
        });
        for (const h of response.data.holidays ?? []) {
            const key = `${h.holidayCalendarID}-${h.holidayDate}`;
            merged.set(key, h);
        }
    }
    return Array.from(merged.values()).sort((a, b) => a.holidayDate.localeCompare(b.holidayDate));
}
